import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { programAssignmentsApi, programsApi, type Program, type ThemeVisibility } from '@/api/programs'
import { getErrorMessage } from '@/utils/error'

const VISIBILITY_OPTIONS: ThemeVisibility[] = ['hidden', 'titles', 'full']

/** Подпись программы в списке — та же, что была у пунктов выпадающего списка */
const programOptionLabel = (program: Program): string =>
  `${program.name}${program.level ? ` · ${program.level}` : ''}${program.is_system ? ' · EasyA' : ''}`

/**
 * Назначить программу ученику из его карточки.
 *
 * Обратный ход к назначению из конструктора: там выбирают получателя при
 * известной программе, здесь — программу при известном ученике. Учитель
 * приходит к этому вопросу именно из карточки, и уводить его в библиотеку
 * ради одного действия незачем.
 */
export function AssignProgramDialog({
  open,
  studentId,
  onClose,
  onAssigned,
}: {
  open: boolean
  studentId: number
  onClose: () => void
  onAssigned: () => void
}) {
  const { t } = useTranslation()

  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(false)
  const [program, setProgram] = useState<Program | null>(null)
  const [visibility, setVisibility] = useState<ThemeVisibility>('titles')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Одной страницы мало: у учителя со ста двадцатью программами двадцати
      // в списке просто не было. Архивные не предлагаем: назначить их нельзя
      const result = await programsApi.listAll()
      setPrograms(result.filter((item) => item.status !== 'archived'))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!open) return
    setProgram(null)
    setError(null)
    void load()
  }, [open, load])

  const handleAssign = async () => {
    if (program === null) return

    setSaving(true)
    setError(null)
    try {
      await programAssignmentsApi.assign(program.id, {
        recipient_type: 'student',
        recipient_id: studentId,
        visibility_default: visibility,
      })
      onAssigned()
      onClose()
    } catch (err) {
      // Лимит назначений и «уже есть активная» приходят сюда: страничный Alert
      // рисуется под открытым диалогом, и учитель его не увидел бы
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('programs.assignTitle')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rounded" height={56} sx={{ mt: 1 }} />
        ) : programs.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('programs.empty')}
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Автодополнение, а не выпадающий список: сотня программ прокручивается
                глазами, а искать в ней было нечем. Пустой результат поиска — не `programs.empty`:
                тот текст зовёт создать первую программу, а здесь они есть — просто
                ни одна не подошла под запрос */}
            <Autocomplete
              fullWidth
              size="small"
              options={programs}
              value={program}
              disabled={saving}
              onChange={(_, value) => setProgram(value)}
              getOptionLabel={(option) => programOptionLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t('programs.emptyFiltered')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('programs.title')}
                  placeholder={t('programs.searchPlaceholder')}
                />
              )}
            />

            <FormControl fullWidth size="small">
              <InputLabel>{t('programs.fields.visibility')}</InputLabel>
              <Select
                label={t('programs.fields.visibility')}
                value={visibility}
                disabled={saving}
                onChange={(event) => setVisibility(event.target.value as ThemeVisibility)}
              >
                {VISIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`programs.visibility.${option}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={program === null || saving} onClick={() => void handleAssign()}>
          {t('programs.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
